import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { AccessControlService } from 'src/app/services/access-control.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import {
  PROJECT_FINANCE_DEFAULT_CURRENCY,
  ProjectFinanceExcelFile,
  ProjectInvoice,
  projectInvoiceStatusClass,
  projectInvoiceStatusLabel
} from 'src/app/models/entities/project-finance';

@Component({
  selector: 'projects-finance-income',
  imports: [LoaderComponent, NgbTooltipModule, CurrencyPipe],
  templateUrl: './projects-finance-income.component.html',
  styleUrl: './projects-finance-income.component.scss'
})
export class ProjectsFinanceIncomeComponent implements OnInit, OnDestroy {
  @Input() projectId!: number;

  invoices: ProjectInvoice[] = [];
  canModify = false;
  isLoading = false;
  isExporting = false;
  busyInvoiceId: number | null = null;

  readonly currency = PROJECT_FINANCE_DEFAULT_CURRENCY;
  readonly statusLabel = projectInvoiceStatusLabel;
  readonly statusClass = projectInvoiceStatusClass;

  private destroy$ = new Subject<void>();

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(profileCode, 'PROJECTS_FINANCE', 'modify');
      });
    this.loadInvoices();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatDate(value: string | null | undefined, withTime: boolean = false): string {
    return this.commonService.formatDateValue(value, withTime);
  }

  isOverdue(invoice: ProjectInvoice): boolean {
    return invoice.status === 'OVERDUE' || invoice.status === 'LOCKED';
  }

  canSend(invoice: ProjectInvoice): boolean {
    return this.canModify && !!invoice.attachment && !invoice.sentDate && this.busyInvoiceId === null;
  }

  sendTooltip(invoice: ProjectInvoice): string {
    if (!this.canModify) return 'Your profile cannot send invoices';
    if (!invoice.attachment) return 'Attach the invoice to enable sending';
    if (invoice.sentDate) return 'Invoice already sent — replace the attachment to send it again';
    return 'Send invoice to client';
  }

  editInstalment(invoice: ProjectInvoice): void {
    this.routeService.projectsFinanceInstalmentUpdate(this.projectId, invoice.projectInvoiceId);
  }

  openAttachment(invoice: ProjectInvoice): void {
    const url = invoice.attachment?.url;
    if (!url) {
      this.alertNotificationService.warning('File URL not available');
      return;
    }
    this.projectFinanceService.attachmentPublicUrl(url).subscribe({
      next: (publicUrl: string) => {
        if (!publicUrl) {
          this.alertNotificationService.error('Error downloading file');
          return;
        }
        window.open(publicUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Error downloading the invoice:', error);
        this.alertNotificationService.error('Error downloading file');
      }
    });
  }

  confirmSend(invoice: ProjectInvoice): void {
    if (!this.canSend(invoice)) return;
    this.confirmationDialogService.open(
      `Send the ${invoice.instalmentTypeName} invoice to the client by email?`,
      () => this.sendInvoice(invoice)
    );
  }

  exportExcel(): void {
    this.isExporting = true;
    this.projectFinanceService.exportInvoicesExcel(this.projectId).subscribe({
      next: (response) => {
        this.isExporting = false;
        this.downloadExcel(response?.data);
      },
      error: (error) => {
        this.isExporting = false;
        console.error('Error exporting finance income:', error);
        this.alertNotificationService.error('Error downloading Excel file');
      }
    });
  }

  private sendInvoice(invoice: ProjectInvoice): void {
    this.busyInvoiceId = invoice.projectInvoiceId;
    this.projectFinanceService.sendInvoice(this.projectId, invoice.projectInvoiceId).subscribe({
      next: () => {
        this.busyInvoiceId = null;
        this.alertNotificationService.success('Invoice sent to the client');
        this.loadInvoices();
      },
      error: (error) => {
        this.busyInvoiceId = null;
        console.error('Error sending invoice:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error sending invoice'));
      }
    });
  }

  private downloadExcel(file: ProjectFinanceExcelFile | undefined): void {
    if (!file?.base64) {
      this.alertNotificationService.error('Unable to download file: missing base64 data');
      return;
    }
    this.commonService.downloadBase64File(file.base64, file.fileName, file.mimeType);
    this.alertNotificationService.success('File Excel downloaded successfully');
  }

  private loadInvoices(): void {
    this.isLoading = true;
    this.projectFinanceService.listInvoices(this.projectId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.invoices = this.projectFinanceService.readResponseKey<ProjectInvoice[]>(response, 'projectInvoices') ?? [];
      },
      error: (error) => {
        this.isLoading = false;
        this.invoices = [];
        console.error('Error loading finance income:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error loading finance income'));
      }
    });
  }
}
